import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const MONEY_DECIMALS = 5;
export const MONEY_SCALE = 10 ** MONEY_DECIMALS;

function getFlexibleFractionDigits(value, fallbackDecimals = null) {
  if (Number.isInteger(fallbackDecimals)) {
    return {
      minimumFractionDigits: fallbackDecimals,
      maximumFractionDigits: fallbackDecimals,
    };
  }

  return {
    minimumFractionDigits: 2,
    maximumFractionDigits: MONEY_DECIMALS,
  };
}

export function roundMoney(value, decimals = MONEY_DECIMALS) {
  const factor = 10 ** decimals;
  return Math.round((Number(value) || 0) * factor) / factor;
}

export function moneyToUnits(value) {
  return Math.round((parseCurrency(value) || 0) * MONEY_SCALE);
}

/**
 * Formata valor monetário usando pt-BR/BRL por padrão.
 */
export function formatCurrency(value, currency = "BRL", locale = "pt-BR", decimals = null) {
  const amount = roundMoney(value);
  const fractionDigits = getFlexibleFractionDigits(amount, decimals);

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      ...fractionDigits,
    }).format(amount);
  } catch (_) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      ...fractionDigits,
    }).format(amount);
  }
}

/**
 * Formata valor numérico para padrão brasileiro sem símbolo R$ e sem sinal
 */
export function formatAmount(value, decimals = null) {
  const absValue = Math.abs(roundMoney(value));
  const fractionDigits = getFlexibleFractionDigits(absValue, decimals);

  return new Intl.NumberFormat("pt-BR", {
    ...fractionDigits,
  }).format(absValue);
}

/**
 * Formata data para padrão brasileiro (DD/MM/YYYY)
 */
export function formatDate(date) {
  return new Intl.DateTimeFormat("pt-BR").format(new Date(date));
}

/**
 * Formata data e hora para padrão brasileiro
 */
export function formatDateTime(date) {
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(date));
}

/**
 * Parseia valor monetário de string para número
 */
export function parseCurrency(value) {
  if (typeof value === "number") return value;
  const cleaned = String(value || "").replace(/[^\d,.-]/g, "");
  if (!cleaned) return 0;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");

  if (lastComma > lastDot) {
    return parseFloat(cleaned.replace(/\./g, "").replace(",", ".")) || 0;
  }

  if (lastDot > lastComma) {
    return parseFloat(cleaned.replace(/,/g, "")) || 0;
  }

  return parseFloat(cleaned) || 0;
}

/**
 * Gera cor baseada em string (para categorias)
 */
export function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
}

export function normalizeCategoryName(value) {
  return String(value || "").trim().toLocaleUpperCase("pt-BR");
}

export function normalizeUppercase(value) {
  return String(value || "").trim().toLocaleUpperCase("pt-BR");
}

export function formatCategoryName(value) {
  const normalized = normalizeCategoryName(value);
  return normalized || "Sem categoria";
}
